import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { cognitoConfig } from './config';
import { mmkv, storage } from '@/storage/mmkv';
import type { AuthUser } from '@/types/auth';

const KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  ID_TOKEN: 'auth_id_token',
  REFRESH_TOKEN: 'auth_refresh_token',
} as const;

// Persistent, SYNCHRONOUS storage so Cognito sessions survive app restarts.
// amazon-cognito-identity-js requires a synchronous storage (ICognitoStorage);
// without one it defaults to in-memory storage and the user is silently logged
// out on every relaunch. MMKV is synchronous (AsyncStorage is not, so it can't
// be used here). clear() only removes Cognito's own keys, never app data.
const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.';
export const cognitoStorage = {
  getItem: (key: string): string | null => mmkv.getString(key) ?? null,
  setItem: (key: string, value: string): void => mmkv.setString(key, value),
  removeItem: (key: string): void => {
    mmkv.delete(key);
  },
  clear: (): void => {
    storage
      .getAllKeys()
      .filter((k) => k.startsWith(COGNITO_KEY_PREFIX))
      .forEach((k) => storage.remove(k));
  },
};

// Construct the Cognito pool lazily: in dev/mock mode the config is empty and
// CognitoUserPool throws on empty IDs. Returns null when auth isn't configured;
// none of these methods run in dev mode (AuthContext short-circuits on isDevMode).
let _userPool: CognitoUserPool | null = null;
function getUserPool(): CognitoUserPool | null {
  if (_userPool) return _userPool;
  if (!cognitoConfig.userPoolId || !cognitoConfig.clientId) return null;
  _userPool = new CognitoUserPool({
    UserPoolId: cognitoConfig.userPoolId,
    ClientId: cognitoConfig.clientId,
    Storage: cognitoStorage,
  });
  return _userPool;
}

function makeCognitoUser(email: string): CognitoUser {
  const pool = getUserPool();
  if (!pool) throw new Error('Auth is not configured');
  // Must pass the same sync MMKV storage as the pool: CognitoUser defaults to
  // async AsyncStorage otherwise, so sign-in would cache tokens where the pool's
  // synchronous getCurrentUser() can't read them on cold start (→ logged out).
  return new CognitoUser({ Username: email, Pool: pool, Storage: cognitoStorage });
}

function storeSession(session: CognitoUserSession): void {
  mmkv.setString(KEYS.ACCESS_TOKEN, session.getAccessToken().getJwtToken());
  mmkv.setString(KEYS.ID_TOKEN, session.getIdToken().getJwtToken());
  mmkv.setString(KEYS.REFRESH_TOKEN, session.getRefreshToken().getToken());
}

function clearSession(): void {
  mmkv.delete(KEYS.ACCESS_TOKEN);
  mmkv.delete(KEYS.ID_TOKEN);
  mmkv.delete(KEYS.REFRESH_TOKEN);
}

function parseUserFromSession(session: CognitoUserSession, cognitoUser: CognitoUser): AuthUser {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    id: payload['sub'] as string,
    email: payload['email'] as string,
    username: (payload['cognito:username'] ?? payload['email']) as string,
    emailVerified: payload['email_verified'] === true || payload['email_verified'] === 'true',
  };
}

export interface SignUpOpts {
  kind: 'email' | 'phone';
  acceptedTermsAt: string;
}

// Normalize an identifier so the value stored as the alias matches at confirm/sign-in.
// Email → trimmed + lowercased; phone → E.164-ish (digits + a single leading '+').
export function normalizeIdentifier(raw: string): string {
  const s = raw.trim();
  if (s.includes('@')) return s.toLowerCase();
  const hasPlus = s.startsWith('+');
  const digits = s.replace(/[^\d]/g, '');
  return hasPlus ? `+${digits}` : digits;
}

// Preserve the Cognito exception name (e.g. 'UserNotConfirmedException') on a fresh
// Error so callers can branch on `.code` — amazon-cognito-identity-js exposes it as
// `.code`/`.name`, both of which we'd otherwise lose by rebuilding from `.message`.
export function makeAuthError(err: Error): Error {
  const raw = err as Error & { code?: string };
  const e = new Error(err.message) as Error & { code?: string };
  e.code = raw.code ?? err.name;
  return e;
}

export const CognitoService = {
  signUp(
    identifier: string,
    password: string,
    opts: SignUpOpts = { kind: 'email', acceptedTermsAt: new Date().toISOString() },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const value = normalizeIdentifier(identifier);
      const identifierAttr = new CognitoUserAttribute({
        Name: opts.kind === 'phone' ? 'phone_number' : 'email',
        Value: value,
      });
      const termsAttr = new CognitoUserAttribute({
        Name: 'custom:terms_accepted_at',
        Value: opts.acceptedTermsAt,
      });
      const pool = getUserPool();
      if (!pool) {
        reject(new Error('Auth is not configured'));
        return;
      }
      // This pool uses UsernameAttributes (email/phone) — the username MUST be the
      // email/phone itself; Cognito then auto-generates the internal cognito:username
      // UUID. (Passing a UUID fails: "Username should be either an email or a phone number".)
      pool.signUp(value, password, [identifierAttr, termsAttr], [], (err) => {
        if (err) {
          reject(makeAuthError(err));
        } else {
          resolve();
        }
      });
    });
  },

  confirmSignUp(identifier: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Confirm via the email/phone alias — Cognito resolves it to the user.
      makeCognitoUser(normalizeIdentifier(identifier)).confirmRegistration(code, true, (err) => {
        if (err) {
          reject(makeAuthError(err));
        } else {
          resolve();
        }
      });
    });
  },

  // Re-send the sign-up confirmation code to the email/phone alias. Used when an
  // account exists but was never confirmed (e.g. the user closed the app mid-signup,
  // then later tried to sign in → UserNotConfirmedException).
  resendConfirmation(identifier: string): Promise<void> {
    return new Promise((resolve, reject) => {
      makeCognitoUser(normalizeIdentifier(identifier)).resendConfirmationCode((err) => {
        if (err) {
          reject(makeAuthError(err));
        } else {
          resolve();
        }
      });
    });
  },

  signIn(email: string, password: string): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      const username = normalizeIdentifier(email);
      const cognitoUser = makeCognitoUser(username);
      const authDetails = new AuthenticationDetails({ Username: username, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess(session) {
          storeSession(session);
          resolve(parseUserFromSession(session, cognitoUser));
        },
        onFailure(err) {
          reject(makeAuthError(err));
        },
        newPasswordRequired() {
          reject(new Error('New password required. Please contact support.'));
        },
      });
    });
  },

  signOut(): Promise<void> {
    return new Promise((resolve) => {
      const cognitoUser = getUserPool()?.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      clearSession();
      resolve();
    });
  },

  deleteUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = getUserPool()?.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('Not signed in'));
        return;
      }
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          reject(new Error('Session expired. Please sign in again.'));
          return;
        }
        cognitoUser.deleteUser((delErr) => {
          if (delErr) {
            reject(new Error(delErr.message));
            return;
          }
          cognitoUser.signOut();
          clearSession();
          resolve();
        });
      });
    });
  },

  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      makeCognitoUser(email).forgotPassword({
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(new Error(err.message));
        },
        inputVerificationCode() {
          resolve();
        },
      });
    });
  },

  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      makeCognitoUser(email).confirmPassword(code, newPassword, {
        onSuccess() {
          resolve();
        },
        onFailure(err) {
          reject(new Error(err.message));
        },
      });
    });
  },

  getCurrentUser(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      const cognitoUser = getUserPool()?.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          clearSession();
          resolve(null);
          return;
        }
        storeSession(session);
        resolve(parseUserFromSession(session, cognitoUser));
      });
    });
  },

  getAccessToken(): Promise<string | null> {
    return Promise.resolve(mmkv.getString(KEYS.ACCESS_TOKEN) ?? null);
  },

  /**
   * Synchronous access-token read. Tries the in-memory Cognito session first
   * (via getUserPool()?.getCurrentUser().getSession callback, which fires synchronously
   * when the session is already cached). Falls back to the MMKV-cached token
   * that storeSession() writes on every sign-in / session refresh.
   */
  getAccessTokenSync(): string | null {
    const user = getUserPool()?.getCurrentUser();
    if (user) {
      let token: string | null = null;
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session && session.isValid()) {
          token = session.getAccessToken().getJwtToken();
        }
      });
      if (token) return token;
    }
    // Fallback: use the MMKV-cached token written by storeSession()
    return mmkv.getString(KEYS.ACCESS_TOKEN) ?? null;
  },
};
